/**
 * Клиентский error tracking через Sentry-compatible SDK (@sentry/react).
 *
 * Ingest — self-hosted Rustrak (DSN из VITE_SENTRY_DSN). Пустой DSN = выключено.
 * Используется для: JS-ошибки, React Error Boundary, sample performance traces,
 * scrub секретов security (PIN/TOTP/recovery) в beforeSend.
 */

import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";

/**
 * Инициализация клиентского error tracking (Rustrak / Sentry-compatible).
 *
 * - DSN: VITE_SENTRY_DSN (пример http://key@localhost:8081/1)
 * - Environment: VITE_APP_ENV || MODE
 * - TracesSampleRate: VITE_SENTRY_TRACES_SAMPLE_RATE (default 0.1)
 * - В development нужен VITE_SENTRY_ENABLED=true
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_APP_ENV || import.meta.env.MODE;

  if (!dsn) {
    console.warn("VITE_SENTRY_DSN не задан (Rustrak). Мониторинг ошибок отключён.");
    return;
  }

  Sentry.init({
    dsn,
    environment,
    
    // Настройки производительности
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "0.1"), // 10% транзакций
    
    // Настройки релизов
    release: import.meta.env.VITE_APP_VERSION || undefined,
    
    // Интеграции: подменяем стандартный Breadcrumbs — иначе при любом console.error
    // Sentry вызывает safeJoin(args) до вызова нативного console; аргументы React.lazy /
    // React 19 иногда несводимы к строке → TypeError и ломается сам лог (home + MapLibreMap).
    integrations: (defaults) => [
      ...defaults.map((integration) =>
        integration.name === "Breadcrumbs"
          ? Sentry.breadcrumbsIntegration({ console: false })
          : integration,
      ),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.globalHandlersIntegration({
        onunhandledrejection: true,
        onerror: true,
      }),
    ],
    
    // Настройки фильтрации
    beforeSend(event, hint) {
      // Фильтрация известных ошибок, которые не требуют внимания
      if (event.exception) {
        const error = hint.originalException;
        
        // Игнорируем ошибки от расширений браузера
        if (error && typeof error === "object" && "message" in error) {
          const message = (error as Error).message;
          if (
            message.includes("chrome-extension://") ||
            message.includes("moz-extension://") ||
            message.includes("safari-extension://")
          ) {
            return null;
          }
        }
      }

      // Scrub секретов модуля security (PIN, TOTP, recovery, токены)
      const scrub = (value: unknown): unknown => {
        if (typeof value === "string") {
          return value
            .replace(/\b\d{4,8}\b/g, "[redacted-digits]")
            .replace(/otpauth:\/\/[^\s"']+/gi, "[redacted-otpauth]")
            .replace(/[A-F0-9]{4}(?:-[A-F0-9]{4}){3}/gi, "[redacted-recovery]");
        }
        if (Array.isArray(value)) return value.map(scrub);
        if (value && typeof value === "object") {
          const out: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            const lk = k.toLowerCase();
            if (
              ["pin", "password", "totp", "code", "secret", "mfa_token", "unlock_token", "access_token", "recovery_codes", "otpauth_uri"].includes(lk) ||
              lk.includes("pin") ||
              lk.includes("secret") ||
              lk.includes("token")
            ) {
              out[k] = "[redacted]";
            } else {
              out[k] = scrub(v);
            }
          }
          return out;
        }
        return value;
      };
      if (event.extra) event.extra = scrub(event.extra) as typeof event.extra;
      if (event.contexts) event.contexts = scrub(event.contexts) as typeof event.contexts;
      if (event.request?.data) {
        event.request.data = scrub(event.request.data) as typeof event.request.data;
      }
      
      return event;
    },
    
    // Настройки окружения
    ignoreErrors: [
      // Игнорируем известные ошибки браузеров
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      // Игнорируем ошибки от внешних скриптов
      /^Script error\.?$/,
    ],
    
    // Настройки для production
    enabled: environment !== "development" || import.meta.env.VITE_SENTRY_ENABLED === "true",
  });
}

/**
 * Обертка для React компонентов с отслеживанием ошибок.
 * 
 * Использование:
 * ```tsx
 * export default Sentry.withErrorBoundary(App, {
 *   fallback: <ErrorFallback />,
 *   showDialog: true,
 * });
 * ```
 */
export const SentryErrorBoundary = Sentry.withErrorBoundary;

/**
 * Утилита для ручного логирования ошибок.
 * 
 * @param error - Ошибка для логирования
 * @param context - Дополнительный контекст
 */
export function captureException(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    contexts: {
      custom: context || {},
    },
  });
}

/**
 * Утилита для логирования сообщений.
 * 
 * @param message - Сообщение для логирования
 * @param level - Уровень логирования (info, warning, error)
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info"
) {
  Sentry.captureMessage(message, level);
}

/**
 * Утилита для установки пользовательского контекста.
 * 
 * @param user - Информация о пользователе
 */
export function setUser(user: {
  id?: string;
  email?: string;
  username?: string;
}) {
  Sentry.setUser(user);
}

/**
 * Утилита для очистки пользовательского контекста (при выходе).
 */
export function clearUser() {
  Sentry.setUser(null);
}

/**
 * Утилита для установки дополнительного контекста.
 * 
 * @param key - Ключ контекста
 * @param context - Данные контекста
 */
export function setContext(key: string, context: Record<string, unknown>) {
  Sentry.setContext(key, context);
}

